import React from 'react';
import Container from './shared/Container';
import useScrollFade from '../hooks/useScrollFade';

const groups = [
  {
    number: "01",
    challenges: [
      {
        title: "Document Overload",
        desc: "PDFs, handwritten forms, scanned records. Every format, every day, each demanding manual effort.",
      },
      {
        title: "Costly Keying Mistakes",
        desc: "Keying errors cascade downstream: wrong codes and missed dates delay resolution.",
      },
      {
        title: "Misrouted Documents",
        desc: "Inconsistent handling routes documents to the wrong queue, creating delays and missed SLAs.",
      },
    ],
    solution: "Vermillion Intake",
    solutionDesc: "Classifies every document on arrival, extracts structured fields with 98%+ accuracy, and routes uncertain extractions for human review. No guessing.",
    icon: (
      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
  },
  {
    number: "02",
    challenges: [
      {
        title: "Inconsistent Decisions",
        desc: "Same case, different reviewer, different outcome. Inconsistency creates risk and compliance exposure.",
      },
      {
        title: "Guidelines Lost in Translation",
        desc: "Policy interpretation varies by reviewer, and general-purpose AI models cannot reliably enforce the current policy version.",
      },
      {
        title: "Compliance Exposure",
        desc: "Decisions made informally leave no traceable record, giving regulators and auditors nothing to verify.",
      },
    ],
    solution: "Vermillion Evident",
    solutionDesc: "Converts governing documents into deterministic, executable programs: same input, same output, every time, with a full audit trail.",
    icon: (
      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
      </svg>
    ),
  },
];

const ProblemSolution = () => {
  const [ref, visible] = useScrollFade();

  return (
    <section className="py-20 sm:py-28 bg-surface-secondary">
      <Container>
        <div ref={ref} className={`scroll-fade${visible ? ' visible' : ''}`}>

          {/* Header */}
          <div className="mb-10">
            <p className="text-xs font-semibold tracking-[0.12em] uppercase text-vermillion-600 mb-3">
              From challenge to solution
            </p>
            <h2 className="text-[1.75rem] sm:text-[2rem] leading-tight font-bold tracking-tight text-gray-900">
              Built for the problems you face every day
            </h2>
          </div>

          {/* Column labels — desktop only */}
          <div className="hidden lg:grid lg:grid-cols-2 pb-3 border-b border-gray-200">
            <span className="text-xs font-semibold tracking-[0.1em] uppercase text-gray-400">The Challenges</span>
            <span className="text-xs font-semibold tracking-[0.1em] uppercase text-gray-400 pl-10">Our Solution</span>
          </div>

          {/* Groups */}
          {groups.map((group) => (
            <div
              key={group.number}
              className="grid grid-cols-1 lg:grid-cols-2 lg:items-center border-b border-gray-150 py-8 gap-6 lg:gap-0"
            >
              {/* Challenges column */}
              <div className="flex gap-5 lg:pr-10">
                <span className="text-xs font-bold tracking-[0.06em] text-vermillion-600 shrink-0 pt-[0.2rem] w-8">
                  {group.number}
                </span>
                <div className="flex flex-col gap-3">
                  {group.challenges.map((challenge, i) => (
                    <div key={i} className={i > 0 ? "pt-3 border-t border-gray-100" : ""}>
                      <h3 className="text-sm font-semibold text-gray-900">{challenge.title}</h3>
                      <p className="mt-1 text-sm text-gray-500 leading-relaxed">{challenge.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Solution column */}
              <div className="flex gap-4 lg:pl-10 lg:border-l lg:border-gray-100">
                <div className="flex-shrink-0 text-vermillion-600 mt-0.5">
                  {group.icon}
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">{group.solution}</p>
                  <p className="mt-1.5 text-sm text-gray-500 leading-relaxed">{group.solutionDesc}</p>
                </div>
              </div>
            </div>
          ))}

        </div>
      </Container>
    </section>
  );
};

export default ProblemSolution;
